import type {
  FormEventHandler,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from "react"

type VacationOverviewCardProps = {
  actionExpanded: boolean
  actionHasPopup?: "dialog"
  actionLabel: string
  children: ReactNode
  onAction: () => void
  title: string
}

export function VacationOverviewCard({
  actionExpanded,
  actionHasPopup,
  actionLabel,
  children,
  onAction,
  title,
}: VacationOverviewCardProps) {
  return (
    <section className="overflow-hidden rounded-[8px] border border-[#e5e5e5] bg-white">
      <header className="flex min-h-16 items-center gap-3 border-b border-[#e5e5e5] px-4 py-4">
        <h3 className="min-w-0 flex-1 text-base font-semibold uppercase leading-5 text-black">{title}</h3>
        <button
          aria-expanded={actionExpanded}
          aria-haspopup={actionHasPopup}
          aria-label={actionLabel}
          className="inline-flex h-8 shrink-0 cursor-pointer items-center gap-1.5 overflow-hidden rounded-[4px] bg-[#344149] px-3 py-2 text-xs font-medium leading-4 text-white hover:bg-[#2a353c] active:bg-[#20282d] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#337ab7] focus-visible:ring-offset-2"
          onClick={onAction}
          type="button"
        >
          <AddIcon />
          Add
        </button>
      </header>
      <div className="flex flex-col gap-4 px-4 py-4">{children}</div>
    </section>
  )
}

function AddIcon() {
  return (
    <span aria-hidden="true" className="relative h-[9px] w-[9px] shrink-0 text-white">
      <span className="absolute left-1/2 top-1/2 h-px w-[9px] -translate-x-1/2 -translate-y-1/2 rounded bg-current" />
      <span className="absolute left-1/2 top-1/2 h-[9px] w-px -translate-x-1/2 -translate-y-1/2 rounded bg-current" />
    </span>
  )
}

export function VacationOverviewForm({
  canSubmit,
  children,
  onSubmit,
}: {
  canSubmit: boolean
  children: ReactNode
  onSubmit: FormEventHandler<HTMLFormElement>
}) {
  return (
    <form
      className="grid gap-3 rounded-[4px] border border-[#d5d5d5] bg-[#fafafa] p-3"
      onSubmit={onSubmit}
    >
      {children}
      <button
        className="h-10 justify-self-end rounded bg-[#344149] px-4 text-sm font-bold text-white hover:bg-[#263138] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#337ab7] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        disabled={!canSubmit}
        type="submit"
      >
        Add
      </button>
    </form>
  )
}

type VacationOverviewSelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  children: ReactNode
  id: string
  label: string
}

export function VacationOverviewSelect({
  children,
  id,
  label,
  ...props
}: VacationOverviewSelectProps) {
  return (
    <>
      <label className="sr-only" htmlFor={id}>
        {label}
      </label>
      <select
        {...props}
        className="h-10 min-w-0 rounded border border-[#c7c7c7] bg-white px-3 text-sm font-medium text-[#4a4a4a] outline-none focus:border-[#337ab7] focus:ring-2 focus:ring-[#337ab7]/20"
        id={id}
      >
        {children}
      </select>
    </>
  )
}

type VacationOverviewInputProps = InputHTMLAttributes<HTMLInputElement> & {
  id: string
  label: string
}

export function VacationOverviewInput({
  id,
  label,
  ...props
}: VacationOverviewInputProps) {
  return (
    <>
      <label className="sr-only" htmlFor={id}>
        {label}
      </label>
      <input
        {...props}
        className="h-10 min-w-0 rounded border border-[#c7c7c7] bg-white px-3 text-sm font-medium text-[#4a4a4a] outline-none focus:border-[#337ab7] focus:ring-2 focus:ring-[#337ab7]/20"
        id={id}
      />
    </>
  )
}
